<template>
  <div class="panel">
    <!-- KPIs -->
    <div class="kpi-grid">
      <div v-for="k in kpis" :key="k.label" class="kpi">
        <div class="kpi-top">
          <span class="kpi-label">{{ k.label }}</span>
          <span class="kpi-icon" :style="{ background: k.tint + '1a' }">
            <PortalIcon :name="k.icon" :size="15" :style="{ color: k.tint }" />
          </span>
        </div>
        <div class="kpi-value" :style="{ color: k.tint }">{{ k.value }}</div>
      </div>
    </div>

    <!-- Historial mensual -->
    <div class="p-card">
      <h3 class="p-card-title">Historial de incidencias por mes (últimos 12 meses)</h3>
      <LineChart :data="creadasMes" color="#003ca5" />
      <LineChart :data="concluidasMes" color="#16a34a" />
    </div>

    <!-- Gráficos -->
    <div class="cards-2">
      <div class="p-card">
        <h3 class="p-card-title">Registros por área (top 8)</h3>
        <BarsChart :data="porArea" />
      </div>
      <div class="p-card">
        <h3 class="p-card-title">Por estatus</h3>
        <div class="estatus-row">
          <DonutChart :data="porEstatus" />
          <div class="estatus-legend">
            <div v-for="d in porEstatus" :key="d.name" class="legend-item">
              <span class="legend-dot" :style="{ background: d.color }"></span>
              <span class="legend-name">{{ d.name }}</span>
              <span class="legend-val">{{ d.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vencidas -->
    <div v-if="venc.length" class="p-card">
      <h3 class="p-card-title">Vencidas — requieren atención</h3>
      <div class="venc-list">
        <div v-for="r in venc.slice(0, 6)" :key="r.folio" class="venc-item">
          <PortalIcon name="AlertTriangle" :size="16" style="color: #dc2626" />
          <span class="venc-folio">{{ r.folio }}</span>
          <span class="venc-txt">{{ r.cliente }} · {{ r.asunto }}</span>
          <PortalBadge :text="r.area" :color="colorArea(r.area)" />
          <span class="venc-fecha">venció {{ fmt(r.fechaCompromiso) }}</span>
        </div>
        <button class="link-btn" @click="$emit('navigate', 'registros')">
          Ver todas →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import PortalBadge from "./PortalBadge.vue";
import BarsChart from "./BarsChart.vue";
import DonutChart from "./DonutChart.vue";
import LineChart from "./LineChart.vue";
import { ESTATUS, ESTATUS_COLOR } from "./portal.constants";
import { normEstatus, colorArea, fmt } from "./portal.helpers";
import { porMes } from "./portal.agg";

export default {
  name: "PanelTab",
  components: { PortalIcon, PortalBadge, BarsChart, DonutChart, LineChart },
  props: {
    reg: { type: Array, default: () => [] },
  },
  emits: ["navigate"],
  computed: {
    abiertos() {
      return this.reg.filter(
        (r) =>
          normEstatus(r.estatus) !== "Concluido" &&
          normEstatus(r.estatus) !== "Declinado"
      );
    },
    concluidos() {
      return this.reg.filter((r) => normEstatus(r.estatus) === "Concluido");
    },
    venc() {
      return this.reg.filter((r) => r._sla === "Vencido");
    },
    creadasMes() {
      return porMes(this.reg, "fechaInicial");
    },
    concluidasMes() {
      return porMes(this.reg, "fechaFinal");
    },
    pctConcluido() {
      return this.reg.length
        ? Math.round((this.concluidos.length / this.reg.length) * 100)
        : 0;
    },
    avanceProm() {
      return this.reg.length
        ? Math.round(
            this.reg.reduce((a, r) => a + r._av, 0) / this.reg.length
          )
        : 0;
    },
    kpis() {
      return [
        {
          label: "Incidencias abiertas",
          value: this.abiertos.length,
        },
        {
          label: "% Concluido",
          value: this.pctConcluido + "%",
        },
        {
          label: "Vencidas (compromiso)",
          value: this.venc.length,
        },
        {
          label: "Avance promedio",
          value: this.avanceProm + "%",
        },
      ];
    },
    porArea() {
      const areas = {};
      this.reg.forEach((r) => {
        areas[r.area] = (areas[r.area] || 0) + 1;
      });
      return Object.entries(areas)
        .map(([name, value]) => ({ name, value, color: colorArea(name) }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);
    },
    porEstatus() {
      return ESTATUS.map((e) => ({
        name: e,
        value: this.reg.filter((r) => normEstatus(r.estatus) === e).length,
        color: ESTATUS_COLOR[e],
      })).filter((d) => d.value);
    },
  },
  methods: {
    colorArea,
    fmt,
  },
};
</script>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* KPIs */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.kpi {
  background: var(--pt-card);
  border: 1px solid var(--pt-border);
  border-radius: 16px;
  padding: 16px;
}
.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--pt-dim);
}
.kpi-icon {
  border-radius: 8px;
  padding: 6px;
  display: inline-flex;
}
.kpi-value {
  font-size: 24px;
  font-weight: 700;
  margin-top: 8px;
  font-variant-numeric: tabular-nums;
}

/* Cards */
.cards-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.p-card {
  background: var(--pt-card);
  border: 1px solid var(--pt-border);
  border-radius: 16px;
  padding: 16px;
}
.p-card-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--pt-text);
  margin: 0 0 12px;
}

/* Estatus donut + legend */
.estatus-row {
  display: flex;
  align-items: center;
  gap: 16px;
}
.estatus-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
}
.legend-name {
  color: var(--pt-dim);
}
.legend-val {
  margin-left: auto;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* Vencidas */
.venc-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.venc-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  padding: 4px 0;
}
.venc-folio {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.venc-txt {
  color: var(--pt-dim);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.venc-fecha {
  font-size: 12px;
  color: var(--pt-muted);
  white-space: nowrap;
}
.link-btn {
  align-self: flex-start;
  background: none;
  border: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #003ca5;
  margin-top: 4px;
  padding: 0;
}

@media (min-width: 1024px) {
  .kpi-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  .cards-2 {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
